import { useMemo, useState } from 'react';
import { Meta, useLazyPatientMetaQuery } from '../../../graphql/graphql';
import { useEffectAsync } from '../../../helpers';
import { DocumentContentSubType } from '../../../Modules/Documents/Enums/documentContentSubType.enum';
import { MetaKey } from '../../../Modules/Meta/Enums/MetaKey.enum';
import { useAppContext } from '../../../Services/AppContext';

interface Diagnosis {
	label: string;
	value: string;
	subType: string;
}

interface PatientDiagnoses {
	mainDiagnosis?: Diagnosis;
	secondaryDiagnoses?: Diagnosis[];
}

export default function usePatientMeta(patientId: string, meta?: Meta[]) {
	const [getPatientMeta] = useLazyPatientMetaQuery();
	const [patientMeta, setPatientMeta] = useState<any>();
	const [patientDiagnosis, setPatientDiagnosis] = useState<
		PatientDiagnoses | undefined
	>(undefined);
	const [isLoading, setIsLoading] = useState(false);
	const { workplaceId } = useAppContext();

	useEffectAsync(async () => {
		await handleLoadMeta();
	}, [patientId]);

	const handleLoadMeta = async (force?: boolean) => {
		setIsLoading(true);
		const data = force
			? await getPatientMeta({ id: patientId }).unwrap()
			: meta
			? { user: { meta: meta } }
			: await getPatientMeta({ id: patientId }).unwrap();
		if (data) {
			if (data.user?.meta) {
				await handleProcessPatientDiagnosis(data.user?.meta);
			}
			setPatientMeta({ data: data });
		}
		setIsLoading(false);
	};

	const handleProcessPatientDiagnosis = async (meta: Meta[]) => {
		const diagnoses = meta.find(
			(item: Meta) => item?.key === 'diagnosis',
		)?.value;

		if (!diagnoses) return;

		setPatientDiagnosis({
			mainDiagnosis: diagnoses.find(
				(item: Diagnosis) =>
					item.subType === DocumentContentSubType.MAIN_DIAGNOSIS,
			),
			secondaryDiagnoses: diagnoses.filter(
				(item: Diagnosis) =>
					item?.subType === DocumentContentSubType.SECONDARY_DIAGNOSIS,
			),
		});
	};

	const personActingOnBehalfOfAPatient = useMemo(() => {
		return patientMeta?.data?.user?.meta?.find(
			(item: any) => item?.key === 'personActingOnBehalfOfAPatient',
		)?.value;
	}, [patientMeta]);

	const getPersonActingOnBehalfOfAPatient = () => {
		let text = '';
		if (personActingOnBehalfOfAPatient?.fullName) {
			text += personActingOnBehalfOfAPatient.fullName;
		}
		if (personActingOnBehalfOfAPatient?.relationship) {
			text += ' (' + personActingOnBehalfOfAPatient.relationship + ')';
		}
		return text;
	};

	const identificationCode = useMemo(() => {
		return patientMeta?.data?.user?.meta?.find(
			(item: any) => item?.key === 'identificationCode',
		)?.value;
	}, [patientMeta]);

	const canCreateCaseOnPermanentMedication = useMemo(() => {
		return patientMeta?.data?.user?.meta?.find(
			(item: any) =>
				item?.key === MetaKey.CAN_CREATE_CASE_ON_PERMANENT_MEDICATION &&
				item?.workplaceId === workplaceId,
		)?.value;
	}, [patientMeta]);

	return {
		personActingOnBehalfOfAPatient,
		hasPersonActingOnBehalfOfAPatient: !!personActingOnBehalfOfAPatient,
		getPersonActingOnBehalfOfAPatient,
		reFetchMeta: () => handleLoadMeta(true),
		metaIsLoading: isLoading,
		patientDiagnosis,
		patientMeta: patientMeta?.data?.user?.meta as Meta[],
		identificationCode,
		canCreateCaseOnPermanentMedication,
	};
}
